import React from "react";
import toast from "react-hot-toast";
import ThemedModal from "../../utilities/ThemedModal";
import LoadingMessage from "../../loaders/LoadingMessage";
import { Controller, useForm } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useMasterAccountLookup } from "../../../hooks/useMasterAccountLookup";
import { useFileMailMutation } from "../../../services/useremails";
import { Modal } from "react-bootstrap";
import { Button } from "../../";
import { MailMessageControlOptions } from "../../../hooks/useMailMessageControls";
import { ClientProjectSearchSelect, MasterAccountSelect } from "../../forms/selects";
import { FileEmailModalForm, FileEmailModalProps } from "./types";
import { MailMessageData } from "../../../types/MailMessageData";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";

// TODO: there is a tiny delay in displaying the master account data in the select box - I'm not happy with it but it will have to do for now

const FileMailModal = ({
    additionalQueryParams,
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    delegate,
    dialogId,
    dismiss,
    proceed,
    show,
    size = 'lg',
    ...rest
}: Readonly<FileEmailModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const [fileMailTrigger, fileMailResult] = useFileMailMutation();
    const { control, formState: { errors }, handleSubmit, reset, setValue, watch } = useForm<FileEmailModalForm>();
    const { data: masterAccount, error, isBusy: isBusyDoingLookup, lookedUpById } = useMasterAccountLookup({ emailAddress: defaultValues?.from?.emailAddress?.address });
    const [isFormLoading] = useDebounce(isBusyDoingLookup, 300);
    const masterAccountId = watch('masterAccountId');

    const isBusy = false;

    const onClose = () : void => {
        dismiss!();
    };

    const onSubmit = (form: FileEmailModalForm) : void => {
        if (!defaultValues) {
            toast.error(`Unable to file email. Incomplete data supplied.`);
            dismiss!();
            return;
        }

        const queryParams = additionalQueryParams ?? { folderId: null, searchTerm: null };
        toast.promise(fileMailTrigger({
            mailId: defaultValues.id,
            userId: delegate?.userId ?? lookedUpById,
            request: form,
            ...queryParams
        }), {
            loading: "Filing mail...",
            error: (err) => err?.data?.message ?? "Unable to file mail.",
            success: (data: MailMessageData) => {
                proceed!(data);
                return `Email has been filed.`
            }
        }).then(() => {});
    };

    React.useEffect(() => {
        if (!masterAccount) {
            return;
        }
        setValue('masterAccountId', masterAccount.masterAccountId, { shouldDirty: true });
    }, [ masterAccount ]);

    return (
        <Modal className="overflow-visible" backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>
                    File Email
                </Modal.Title>
            </ThemedModal.Header>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Body ref={bodyAnimation}>
                    {isFormLoading && (
                        <LoadingMessage text="Checking the sender against our records" />
                    )}
                    {!isFormLoading && (
                        <div className="row gy-2">
                            <div className="col-12">
                                <Controller
                                    name="masterAccountId"
                                    control={control}
                                    rules={{ required: "Master account is required." }}
                                    render={({ field: { ref, value, onChange, onBlur, ...rest } }) => (
                                        <MasterAccountSelect
                                            disableAnimations={true}
                                            errorMessage={errors?.masterAccountId?.message}
                                            value={value}
                                            onChange={(opt: SelectOption<string>) => setValue('masterAccountId', opt.value, { shouldDirty: true })}
                                            {...rest}
                                        />
                                    )}
                                />
                            </div>
                            {masterAccount && masterAccountId && (
                                <div className="col-12">
                                    <Controller
                                        name="projectId"
                                        control={control}
                                        render={({ field: { ref, value, onChange, onBlur, ...rest } }) => (
                                            <ClientProjectSearchSelect
                                                allowCreate={false}
                                                disableAnimations={true}
                                                isClearable={true}
                                                errorMessage={errors?.projectId?.message}
                                                masterAccountId={masterAccount?.masterAccountId}
                                                divisionId={masterAccount.divisionId}
                                                userId={lookedUpById}
                                                value={value}
                                                onChange={(opt: SelectOption<string>) => {
                                                    if (!opt) {
                                                        setValue('projectId', null, { shouldDirty: true });
                                                        return;
                                                    }
                                                    setValue('projectId', opt.value, { shouldDirty: true });
                                                }}
                                                {...rest}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" variant="primary" disabled={isBusy}>File Email</Button>
                    <Button type="button" variant="light" disabled={isBusy} onClick={onClose}>Cancel</Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export function openFileEmailModal(mailMessage: MailMessageData, delegate: UserDelegate | null, opts: MailMessageControlOptions | null = null) : Promise<MailMessageData> {
    return createConfirmation<FileEmailModalProps, MailMessageData>(confirmable(FileMailModal))({
        additionalQueryParams: opts,
        dialogId: 'email_file_modal',
        defaultValues: mailMessage,
        delegate
    });
}